import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = ({ open, onOpenChange, ...props }) => {
  // When the modal opens, scroll the page to the top so the sticky Header /
  // Top-Nav are visible above the dim backdrop (instead of being scrolled
  // off-screen because the user opened the modal from deep in the page).
  React.useEffect(() => {
    if (open) {
      // INSTANT scroll (no smooth) — smooth scroll can fight with the user's
      // momentum scroll on mobile and feel like a freeze.
      try { window.scrollTo({ top: 0, behavior: 'auto' }); }
      catch (e) { window.scrollTo(0, 0); }
    }
  }, [open]);
  return <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: full-screen sheet (no rounded edges, scrollable inside).
        // Desktop: classic centered dialog (max-w-lg, rounded, padded).
        // Use explicit top/right/bottom/left (not inset-0) so each side can be
        // individually overridden by the sm: variants without specificity wars.
        // `no-scrollbar` hides the visible scrollbar but content can still be
        // scrolled via touch / mouse wheel — feels cleaner on every viewport.
        "fixed z-50 grid gap-4 border bg-background shadow-lg duration-200 overflow-y-auto no-scrollbar",
        "top-0 right-0 bottom-0 left-0 w-full h-[100dvh] max-h-[100dvh] rounded-none p-4 translate-x-0 translate-y-0",
        // sm+ (≥640px): centered, capped at viewport - 32px so it ALWAYS fits
        // without internal scroll even on short laptop screens.
        "sm:top-[50%] sm:left-[50%] sm:right-auto sm:bottom-auto sm:w-[min(92vw,520px)] sm:max-w-none sm:h-auto sm:max-h-[min(720px,calc(100dvh-32px))] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:p-6",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
